import type { Auth, UserCredential } from "firebase/auth";
import { GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword } from "firebase/auth";

/**
 * Shared Firebase authentication utilities
 * These functions accept an auth instance as a parameter to allow use across different packages
 */

/**
 * Sign in with Google
 * @param auth - Firebase Auth instance
 * @param loginHint - Optional login hint for Google provider (e.g., "first.last@ohc400.org")
 * @returns UserCredential from Firebase
 */
export async function signInWithGoogle(
  auth: Auth,
  loginHint?: string,
): Promise<UserCredential> {
  try {
    const provider = new GoogleAuthProvider();
    if (loginHint) {
      provider.setCustomParameters({
        login_hint: loginHint,
      });
    }

    const credential = await signInWithPopup(auth, provider);

    const idToken = await credential.user.getIdToken();

    await fetch("/api/login", {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    return credential;
  } catch (error) {
    console.error("Error signing in with Google", error);
    throw error;
  }
}

/**
 * Sign in with email and password
 * @param auth - Firebase Auth instance
 * @param email - User's email address
 * @param password - User's password
 * @returns UserCredential from Firebase
 */
export async function signInWithEmail(
  auth: Auth,
  email: string,
  password: string,
): Promise<UserCredential> {
  try {
    const credential = await signInWithEmailAndPassword(auth, email, password);

    const idToken = await credential.user.getIdToken();

    await fetch("/api/login", {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    return credential;
  } catch (error) {
    console.error("Error signing in with email", error);
    throw error;
  }
}

/**
 * Sign out the current user
 * @param auth - Firebase Auth instance
 */
export async function signOut(auth: Auth): Promise<void> {
  try {
    await auth.signOut();
    await fetch("/api/logout");
  } catch (error) {
    console.error("Error signing out", error);
    throw error;
  }
}

